import { useEffect, useState } from "react";
import { Col, ListGroup, Placeholder, Row } from "react-bootstrap";
import ReactCountryFlag from "react-country-flag";
import { Link, useNavigate, useParams } from "react-router-dom";

import DstButton from "../../components/DstButton/DstButton";
import { useSession } from "../../contexts/SessionContext";
import { formatDateHumanly } from "../../services/TimeService";
import translate from "../../services/Translate";
import { SurveyAnswerResponse } from "./InsightsUserSurvey.d";
import { fetchSingleSurveyAnswer } from "./InsightsUserSurvey.function";
import "./InsightsUserSurvey.scss";

const InsightsUserSurvey = () => {
  const navigate = useNavigate();
  const { language, showAlert } = useSession();
  const { user_id, timestamp } = useParams();
  const [surveyData, setSurveyData] = useState<{ data: SurveyAnswerResponse | null; loading: boolean }>({
    data: null,
    loading: true,
  });

  useEffect(() => {
    if (!user_id || !timestamp) return;
    fetchSingleSurveyAnswer(user_id, timestamp, setSurveyData);
  }, [user_id, timestamp, language, showAlert]);

  return (
    <div className="insights-user-survey p-md-5 p-3">
      <div className="d-flex align-items-center gap-2 mb-4">
        <DstButton
          value={translate(language, "PAGES.INSIGHTS.USER_SURVEY.BACK")}
          clickFunction={() => navigate(`/user/${user_id}`, { state: { tab: "survey-answers" } })}
          btnImageBefore={<span className="dst-icon-arrow-narrow-left me-1 align-text-bottom" />}
          btnClass="me-3"
          variant="light"
        />
        <h1 className="mb-0">{translate(language, "PAGES.INSIGHTS.USER_SURVEY.TITLE")}</h1>
      </div>
      {surveyData.loading ? (
        <Row>
          <Col lg={4} className="mb-4">
            <div className="user-info white-background border rounded p-3 text-center mb-3">
              <div className="d-flex justify-content-center">
                <div className="user-image rounded-circle medium-gray-background" />
              </div>
              <h2 className="mb-0 mt-3">
                <Placeholder as="span" animation="glow">
                  <Placeholder xs={6} />
                </Placeholder>
              </h2>
              <p className="s-regular silver-gray-font">
                <Placeholder as="span" animation="glow">
                  <Placeholder xs={8} />
                </Placeholder>
              </p>
              <div className="xs-regular smoke-gray-font d-flex align-items-center justify-content-center">
                <Placeholder as="span" animation="glow">
                  <Placeholder xs={2} />
                </Placeholder>
              </div>
            </div>
            <div className="white-background border rounded p-3 mb-3 text-center">
              <Placeholder as="h3" animation="glow" className="s-bold smoke-gray-font mb-3">
                <Placeholder xs={5} />
              </Placeholder>
              <Placeholder as="div" animation="glow" className="s-regular">
                <Placeholder xs={7} />
              </Placeholder>
            </div>
            <div className="white-background border rounded p-3 text-center">
              <Placeholder as="h3" animation="glow" className="s-bold smoke-gray-font mb-3">
                <Placeholder xs={5} />
              </Placeholder>
              <Placeholder as="div" animation="glow" className="s-regular">
                <Placeholder xs={8} />
              </Placeholder>
              <Placeholder as="div" animation="glow" className="xs-regular mt-1">
                <Placeholder xs={10} />
              </Placeholder>
            </div>
          </Col>
          <Col lg={8}>
            <div className="white-background border rounded p-3">
              <div className="mb-2">
                <Placeholder as="h2" animation="glow" className="xl-bold smoke-gray-font mb-2">
                  <Placeholder xs={4} />
                </Placeholder>
                <Placeholder as="div" animation="glow" className="xs-regular silver-gray-font">
                  <Placeholder xs={3} />
                </Placeholder>
              </div>
              <h3 className="mb-3">{translate(language, "PAGES.INSIGHTS.USER_SURVEY.RESPONSES_TITLE")}</h3>
              <ListGroup className="responses-list">
                {[1, 2, 3].map((key) => (
                  <ListGroup.Item key={key} className="text-break">
                    <Placeholder as="div" animation="glow" className="s-bold mb-1">
                      <Placeholder xs={10} />
                    </Placeholder>
                    <Placeholder as="div" animation="glow" className="m-regular mb-2">
                      <Placeholder xs={12} />
                    </Placeholder>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>
          </Col>
        </Row>
      ) : !surveyData.data ? (
        <p className="m-regular">{translate(language, "PAGES.INSIGHTS.USER_SURVEY.NO_DATA")}</p>
      ) : (
        <Row>
          <Col lg={4} className="mb-4">
            <div className="user-info white-background border rounded p-3 text-center mb-3">
              <div className="d-flex justify-content-center">
                <img
                  src={surveyData.data.user?.image_url || "/assets/images/user.jpg"}
                  alt="user"
                  className="user-image rounded-circle object-fit-cover"
                />
              </div>
              <h2 className="mb-0 mt-3">
                {surveyData.data.user?.first_name} {surveyData.data.user?.last_name}
              </h2>
              <p className="s-regular silver-gray-font">{surveyData.data.user?.email}</p>
              <div className="xs-regular smoke-gray-font d-flex align-items-center justify-content-center">
                {surveyData.data.user?.language && (
                  <ReactCountryFlag
                    svg
                    className="h3"
                    countryCode={surveyData.data.user.language.split("-")[1]}
                    title={surveyData.data.user.language}
                  />
                )}
              </div>
            </div>
            {surveyData.data.cohort && !surveyData.data.cohort.name && (
              <div className="white-background border rounded p-3 pb-0 mb-3 text-center">
                <h3 className="s-bold smoke-gray-font mb-3">
                  {translate(language, "PAGES.INSIGHTS.USER_SURVEY.COHORT_ID")}: {surveyData.data.cohort.id}
                </h3>
                <p className="xs-regular silver-gray-font mt-1">
                  {translate(language, "PAGES.INSIGHTS.USER_SURVEY.COHORT_DELETED")}
                </p>
              </div>
            )}
            {surveyData.data.cohort?.name && (
              <div className="white-background border rounded p-3 mb-3 text-center">
                <h3 className="s-bold smoke-gray-font mb-3">
                  {translate(language, "PAGES.INSIGHTS.USER_SURVEY.COHORT")}
                </h3>
                <Link
                  to={`/cohort/${surveyData.data.cohort.id}`}
                  className="s-regular smoke-gray-font text-decoration-none d-block mb-2"
                >
                  {surveyData.data.cohort.name}
                </Link>
                {surveyData.data.cohort.dt_start && surveyData.data.cohort.dt_end && (
                  <div className="xs-regular silver-gray-font mb-2">
                    {formatDateHumanly(language, surveyData.data.cohort.dt_start, true)} –{" "}
                    {formatDateHumanly(language, surveyData.data.cohort.dt_end, true)}
                  </div>
                )}
                {surveyData.data.cohort.program_manager && (
                  <div className="xs-regular smoke-gray-font">
                    <div>
                      <span className="s-bold">{translate(language, "PAGES.INSIGHTS.USER_SURVEY.PM")}:</span>{" "}
                      <Link
                        to={`/user/${surveyData.data.cohort.program_manager.id}`}
                        className="gray-font text-decoration-none"
                      >
                        {surveyData.data.cohort.program_manager.full_name}
                      </Link>
                    </div>
                    <div className="xs-regular silver-gray-font mt-0 d-inline-flex align-items-center">
                      <span className="dst-icon-mail-01 me-1" />
                      <a
                        href={`mailto:${surveyData.data.cohort.program_manager.email}`}
                        className="silver-gray-font text-decoration-none"
                      >
                        {surveyData.data.cohort.program_manager.email}
                      </a>
                    </div>
                  </div>
                )}
                {surveyData.data.cohort.cohort_leader && (
                  <div className="xs-regular smoke-gray-font mt-1">
                    <div>
                      <span className="s-bold">{translate(language, "GLOBAL.COHORT_LEADER")}:</span>{" "}
                      <Link
                        to={`/user/${surveyData.data.cohort.cohort_leader.id}`}
                        className="gray-font text-decoration-none"
                      >
                        {surveyData.data.cohort.cohort_leader.full_name}
                      </Link>
                    </div>
                    {surveyData.data.cohort.cohort_leader.email && (
                      <div className="xs-regular silver-gray-font mt-0 d-inline-flex align-items-center">
                        <span className="dst-icon-mail-01 me-1" />
                        <a
                          href={`mailto:${surveyData.data.cohort.cohort_leader.email}`}
                          className="silver-gray-font text-decoration-none"
                        >
                          {surveyData.data.cohort.cohort_leader.email}
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {surveyData.data.sprint && !surveyData.data.sprint.name && (
              <div className="white-background border rounded p-3 text-center">
                <h3 className="s-bold smoke-gray-font mb-3 fw-bold">
                  {translate(language, "PAGES.INSIGHTS.USER_SURVEY.SPRINT_ID")}: {surveyData.data.sprint.id}
                </h3>
                <div className="xs-regular silver-gray-font mt-1">
                  {translate(language, "PAGES.INSIGHTS.USER_SURVEY.SPRINT_DELETED")}
                </div>
              </div>
            )}
            {surveyData.data.sprint?.name && (
              <div className="white-background border rounded p-3 text-center">
                <h3 className="s-bold smoke-gray-font mb-3 fw-bold">
                  {translate(language, "PAGES.INSIGHTS.USER_SURVEY.SPRINT")}
                </h3>
                <div className="s-regular smoke-gray-font">{surveyData.data.sprint.name}</div>
                <div className="xs-regular silver-gray-font mt-1">
                  {formatDateHumanly(language, surveyData.data.sprint.dt_start, true)} –{" "}
                  {formatDateHumanly(language, surveyData.data.sprint.dt_end, true)}
                </div>
              </div>
            )}
          </Col>
          <Col lg={8}>
            <div className="white-background border rounded p-3">
              {surveyData.data.survey_type && (
                <div className="mb-2">
                  <h2 className="xl-bold smoke-gray-font">{surveyData.data.survey_type}</h2>
                  <div className="xs-regular silver-gray-font">
                    {formatDateHumanly(language, new Date(surveyData.data.timestamp * 1000).toISOString(), true)}
                  </div>
                </div>
              )}
              <h3 className="mb-3">{translate(language, "PAGES.INSIGHTS.USER_SURVEY.RESPONSES_TITLE")}</h3>
              {surveyData.data.question_answers?.length ? (
                <ListGroup>
                  {surveyData.data.question_answers.map((question_answer) => (
                    <ListGroup.Item key={question_answer.question_id} className="text-break">
                      <div className="s-bold mb-1">{question_answer.content.question}</div>
                      <div className="m-regular smoke-gray-font mb-2">
                        {question_answer.content.answer ||
                          translate(language, "PAGES.INSIGHTS.USER_SURVEY.NO_RESPONSES")}
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              ) : (
                <p className="m-regular">{translate(language, "PAGES.INSIGHTS.USER_SURVEY.NO_RESPONSES")}</p>
              )}
            </div>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default InsightsUserSurvey;
